import { checkSettingsStore } from '@/services/SettingsStoreUpdateService'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

export const EXTRA_PURCHASE_FIELDS = [
  { key: 'studentName', label: 'ФИО ученика' },
  { key: 'studentEmail', label: 'Почта ученика' },
  { key: 'studentPhone', label: 'Номер телефона учащегося' },
  { key: 'password', label: 'Пароль' },
  { key: 'parentName', label: 'ФИО родителя' },
  { key: 'parentPhone', label: 'Номер родителя' },
  { key: 'parentEmail', label: 'Почта родителя' },
  { key: 'parentPassword', label: 'Пароль родителя' },
  { key: 'customText', label: 'Текстовое поле' },
]

const FIELD_ORDER = EXTRA_PURCHASE_FIELDS.map((f) => f.key)

interface ExtraFieldRule {
  key: string
  state: boolean
  tooltip: string
}

export const useGetExtraPurchaseFields = (): {
  extraFields: string[]
  toggleField: (fieldKey: string) => void
  fieldsRules: ExtraFieldRule[]
} => {
  const isSmsIntegration = useSelector(
    (store: any) => (store.settings?.sms_integration_enabled === '1' && (store.settings?.confirmationByPhoneNumber === '1' || store.settings?.confirmationByTelegram === '1')),
  )

  const registrationType = useSelector((store: any) =>
    store.settings?.registrationTypeForCatalog === 'email' ? 'email' : 'phone',
  ) as 'phone' | 'email'

  const requiredNumberConfirmation = useSelector((store: any) =>
    store.settings?.requiredNumberConfirmationForCatalog
  )

  const filterExtraFieldsWithSMSIntegration = (fields: string[]): string[] => {
    let updated = fields
    if (isSmsIntegration) {
      updated = updated?.filter(
        (item) => !['studentPhone', 'studentEmail'].includes(item),
      )
      if (registrationType === 'email') {
        updated = [...updated, 'studentEmail']
      } else {
        updated = [...updated, 'studentPhone']
        if(requiredNumberConfirmation == 1) {
          updated = updated.filter((item) => item !== 'password');
        }
        else{
          updated = [...new Set([...updated, 'password'])];
        }
      }
    }
    return updated
  }

  const setRequiredFields = (fields: string[]): string[] => {
    let updated = fields
    const hasStudentPhone = updated.includes('studentPhone')
    const hasStudentEmail = updated.includes('studentEmail')

    updated =
      hasStudentPhone || hasStudentEmail
        ? updated
        : [...updated, 'studentEmail']

    if (
      updated.includes('parentPhone') &&
      !updated.includes('parentPassword')
    ) {
      updated = [...updated, 'parentPassword']
    }

    return updated
  }

  const extraFieldsStore = useSelector((store: any) => {
    try {
      checkSettingsStore()
      let updated = JSON.parse(
        store.settings?.extraPurchaseFields ?? '[]',
      ) as string[]

      updated = setRequiredFields(updated)
      updated = filterExtraFieldsWithSMSIntegration(updated)

      // Сортировка по порядку из EXTRA_PURCHASE_FIELDS
      updated = updated.sort((a, b) => {
        return FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b)
      })

      return updated
    } catch {
      return ['studentEmail']
    }
  })

  const [fields, setFields] = useState<string[]>(extraFieldsStore)

  useEffect(() => {
    checkSettingsStore()
    setFields(extraFieldsStore)
  }, [extraFieldsStore.length, registrationType, isSmsIntegration])

  const toggleField = (fieldKey: string) => {
    setFields((prev) => {
      let updated = [...prev]

      if (prev.find((item) => item === fieldKey)) {
        updated = updated.filter((item) => item !== fieldKey)
      } else {
        updated = [...updated, fieldKey]
      }

      if (
        [
          'studentEmail',
          'studentPhone',
          'parentEmail',
          'parentPhone',
        ]?.includes(fieldKey)
      ) {
        let turnOff = ''
        switch (fieldKey) {
          case 'studentEmail':
            turnOff = 'studentPhone'
            break
          case 'studentPhone':
            turnOff = 'studentEmail'
            break
          case 'parentEmail':
            turnOff = 'parentPhone'
            break
          case 'parentPhone':
            turnOff = 'parentEmail'
            break
        }

        updated = updated.filter((item) => item !== turnOff)
      }
      updated = setRequiredFields(updated)
      updated = filterExtraFieldsWithSMSIntegration(updated)

      return updated
    })
  }

  const getExtraFieldsRules = (): ExtraFieldRule[] => {
    let rules: ExtraFieldRule[] = []
    if (isSmsIntegration) {
      fields?.filter((item) => !['studentPhone', 'studentEmail'].includes(item))
      if (registrationType === 'email') {
        rules = [
          ...rules,
          {
            key: 'studentEmail',
            state: true,
            tooltip: 'Включено в настройках СМС интеграции',
          },
          {
            key: 'studentPhone',
            state: false,
            tooltip: 'Скрыто из-за настроек SMS-интеграции',
          },
        ]
      } else {
        rules = [
          ...rules,
          {
            key: 'studentPhone',
            state: true,
            tooltip: 'Включено в настройках СМС интеграции',
          },
          {
            key: 'studentEmail',
            state: false,
            tooltip: 'Скрыто из-за настроек SMS-интеграции',
          },
          {
            key: 'password',
            state: requiredNumberConfirmation == 1 ? false : true,
            tooltip: requiredNumberConfirmation == 1 ? 'Скрыто из-за настроек SMS-интеграции' : 'Включено из-за настроек SMS-интеграции',
          },
        ]
      }
    }
    if (fields.includes('parentPhone') && !fields.includes('parentPassword')) {
      rules = [
        ...rules,
        {
          key: 'parentPassword',
          state: true,
          tooltip: 'Включена регистрация родителя по номеру телефона',
        },
      ]
    }
    return rules
  }

  return {
    extraFields: fields,
    toggleField,
    fieldsRules: getExtraFieldsRules(),
  }
}
